import { useTranslation } from "react-i18next";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { Search } from "lucide-react";
import { useContext, useState } from "react";
import { useGetAllEmployeesInfiniteQuery } from "@/services/(company)/userApi";
import { startCase } from "lodash";
import { useOnInView } from "react-intersection-observer";
import { useDebounceValue } from "usehooks-ts";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner } from "@/components/ui/v2/spinner";
import { BlockHeader } from ".";
import { RightBlock } from "./right-block";
import { Button } from "@/components/ui/button";
import { TeamFormationStepsContext } from "../..";
import { PermissionButton } from "@/components/ui/permission-button";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";

import * as yup from "yup";
import { useFormik } from "formik";
import {
  useAssignTeamMembersMutation,
  useCompanyGetTeamMembersQuery,
} from "@/services/(company)/api.teams";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { PATHS } from "@/routes/paths";
import { MemberAvatar } from "../../../_components/member-avatar";
import { cn } from "@/utils/cn";
import { useLocale } from "@/hooks/useLocale";

// Defining the shape of the team member card used in the formik state and the RightBlock component.
// This includes both the data needed for API calls (userId, roleId) and extra UI data
// (employeeName, profileImage) to avoid having to look up employee details when rendering the selected members list.
export interface ITeamMemberCard {
  userId: number;
  roleId: number | null;
  employeeName: string | null;
  profileImage: string | null;
  position: string | null;
  isTeamManager: boolean;
  createdAt: string | null;
}

const FormSchema = yup.object({
  members: yup.array().of(
    yup.object({
      userId: yup.number().required(),
      roleId: yup.number().nullable(),
      // Storing extra UI data (name, avatar) here for
      // the RightBlock to render without searching the employee list.
      employeeName: yup.string().nullable(),
      profileImage: yup.string().nullable(),
      position: yup.string().nullable(),
      isTeamManager: yup.boolean().nullable(),
      createdAt: yup.string().nullable(),
    }),
  ),
});

type Fields = yup.InferType<typeof FormSchema>;

export const TeamComposer = ({ teamId }: { teamId?: string }) => {
  const { t } = useTranslation(["common", "company", "teams"]);
  const { formatDate } = useLocale();
  const navigate = useNavigate();
  const { data: { data: team } = {}, isFetching: isFetchingTeamMembers } =
    useCompanyGetTeamMembersQuery(
      { teamId: Number(teamId!) },
      { skip: !teamId, refetchOnMountOrArgChange: true },
    );

  const { resetStep } = useContext(TeamFormationStepsContext);
  const [assignTeamMembers, { isLoading: isAssigningTeamMembers }] =
    useAssignTeamMembersMutation();

  const formik = useFormik<Fields>({
    enableReinitialize: true,
    validationSchema: FormSchema,
    initialValues: {
      members:
        team && team?.members?.length > 0
          ? team?.members.map((m) => ({
              userId: m.userId,
              roleId: m.roleId ?? null,
              employeeName:
                m.user?.fullName ?? t("teams:formation.unknown_employee"),
              profileImage: m.user?.profileImage ?? null,
              position:
                m.role?.roleName ?? t("teams:formation.no_role_assigned"),
              isTeamManager: m.role?.isTeamManager,
              createdAt: m.role?.createdAt
                ? formatDate(new Date(m.role.createdAt), "dd MMM, yyyy")
                : null,
            }))
          : [],
    },
    onSubmit: (values) => {
      if (!teamId) {
        toast.error(t("teams:formation.toast_team_id_not_found"));
        return;
      }
      const toastId = toast.loading(t("teams:formation.toast_assigning"));
      if (!values || !values?.members) return;
      const payload = {
        members: values.members.map(({ userId, roleId }) => ({
          userId,
          roleId: roleId ?? null,
        })),
      };
      assignTeamMembers({
        teamId,
        payload,
        isUpdating: team && team?.members?.length > 0,
      })
        .unwrap()
        .then((res) => {
          toast.success(res?.message, { id: toastId });
          navigate(PATHS.COMPANY.COMPANY_TEAMS_PATHS.ROOT);
        })
        .catch((e) => {
          toast.error(e?.data?.message ?? e?.message, { id: toastId });
          console.log(e);
        });
    },
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebounceValue(searchQuery, 500);

  const {
    fetchNextPage,
    data: apiResponse,
    isFetchingNextPage,
    isFetching,
    isLoading: isLoadingEmployees,
  } = useGetAllEmployeesInfiniteQuery({
    limit: 10,
    search: debouncedSearch,
  });

  const employees = apiResponse?.pages.flatMap((p) => p.data) ?? [];
  const infiniteScrollAnchor = useOnInView((inView) => {
    if (inView) fetchNextPage();
  });

  const getMemberIndex = (id: number) =>
    (formik.values.members ?? []).findIndex((m: any) => m.userId === id);

  const toggleMember = (userId: number, roleId?: number | null) => {
    const index = getMemberIndex(userId);
    const currentMembers = formik.values.members ?? [];
    if (index > -1) {
      const newMembers = [...currentMembers];
      newMembers.splice(index, 1);
      formik.setFieldValue("members", newMembers);
    } else {
      const emp = employees.find((e) => e.id === userId);
      if (emp) {
        formik.setFieldValue("members", [
          ...currentMembers,
          { ...emp, userId: emp.id, roleId },
        ]);
      }
    }
  };

  const updateRole = (userId: number, roleId: number) => {
    const index = getMemberIndex(userId);
    formik.setFieldValue(`members[${index}].roleId`, roleId);
  };

  return (
    <form
      onSubmit={formik.handleSubmit}
      className="flex flex-col flex-1 min-h-0 overflow-hidden gap-2"
    >
      <div className="flex flex-wrap flex-1 min-h-0 gap-4">
        {/* LEFT SIDEBAR */}
        <aside
          className={cn(
            "h-144 lg:h-152 w-full min-w-80 lg:w-1/4 flex flex-col overflow-hidden border border-primary rounded-sm",
          )}
        >
          <BlockHeader>
            <InputGroup className="bg-white">
              <InputGroupInput
                value={searchQuery}
                placeholder={t("company:shared.search_employees")}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <InputGroupAddon>
                {isFetching ? (
                  <Spinner size="sm" />
                ) : (
                  <Search className="opacity-80" />
                )}
              </InputGroupAddon>
            </InputGroup>
          </BlockHeader>

          <div className="p-4 flex-1 min-h-0 flex flex-col gap-2 overflow-y-scroll">
            {isLoadingEmployees ? (
              <Spinner />
            ) : employees.length ? (
              <>
                {employees.map((emp) => (
                  <li
                    key={emp.id}
                    className="flex items-center justify-between px-2 py-1.5 border rounded-sm hover:bg-primary/10 relative"
                  >
                    <div
                      className="flex gap-2 items-center cursor-pointer"
                      onClick={() => toggleMember(emp.id, null)}
                    >
                      <MemberAvatar image={emp.profileImage ?? undefined} />
                      <div className="flex flex-col">
                        <p>{startCase(emp?.employeeName?.toLowerCase())}</p>
                        <small className="text-black/50">{emp.position}</small>
                      </div>
                    </div>
                    <Checkbox
                      checked={getMemberIndex(emp.id) > -1}
                      onCheckedChange={() => toggleMember(emp.id, null)}
                    />
                  </li>
                ))}
                <div ref={infiniteScrollAnchor} />
                {isFetchingNextPage && <Spinner wrapperClassName="h-5" />}
              </>
            ) : (
              <small>{t("teams:formation.empty_employees")}</small>
            )}
          </div>
        </aside>
        {/* RIGHT BLOCK */}
        <RightBlock
          onRoleChange={updateRole}
          onRemove={toggleMember}
          selectedMembers={
            formik.values.members as unknown as ITeamMemberCard[]
          }
          isLoading={isFetchingTeamMembers}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button type="reset" variant="outline" onClick={resetStep}>
          {t("teams:formation.back")}
        </Button>
        <PermissionButton
          type="submit"
          section={PLATFORM_SECTION_ENUM.TEAMS}
          action="edit"
          isLoading={isAssigningTeamMembers}
        >
          {t("teams:formation.submit")}
        </PermissionButton>
      </div>
    </form>
  );
};
